import { For, type JSX, Show } from 'solid-js';
import type { ApiBlocker } from '../../lib/blockers';
import type { SendAnswer } from '../../lib/card-answer';
import type { CardSizes } from '../../lib/card-size';
import type { UtilBox } from '../../lib/fleetUtilization';
import { cardEpicsFromJobs } from '../../lib/machine-jobs';
import type { MachineHandlers } from '../../lib/machine-panel';
import type { SendWorkPage } from '../../lib/send-work-page';
import { type CardWrites, MissionCard } from './MissionCard';

// One stretch of the Cards view (#6785, prototype `.mpx-grid`, `:1101`): its Mission Panel
// cards in a grid of 370px-or-wider columns. `MachineGroups` hands it each server's ids;
// the List view's narrow fallback mounts the same grid. KEYED BY ID, looked up in the
// current read, so the 20-second poll updates a card in place under an open menu.

export type MachineCardGridProps = MachineHandlers & {
  readonly ids: () => readonly string[];
  readonly label: () => string;
  readonly byId: ReadonlyMap<string, UtilBox>;
  /** The UNFILTERED fleet: "Running hot" names the machine with the most room in all of it. */
  readonly allBoxes: readonly UtilBox[];
  readonly byTask: ReadonlyMap<string, readonly ApiBlocker[]>;
  readonly sizes: CardSizes;
  readonly canWrite: boolean;
  /** The page's answer call for "Answer here" (#6726). */
  readonly onAnswer: SendAnswer;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  /** The page's ONE Send work controller (#6770): the open form, the send, the Starting rows. */
  readonly send?: SendWorkPage | undefined;
  /** What the cards' job and epic controls write with (#6753). */
  readonly writes?: CardWrites | undefined;
};

export function MachineCardGrid(props: MachineCardGridProps): JSX.Element {
  return (
    <ul class="machine-grid" aria-label={props.label()}>
      <For each={props.ids()}>
        {(id) => (
          <Show when={props.byId.get(id)}>
            {(box) => (
              <MissionCard
                box={box()}
                boxes={props.allBoxes}
                byTask={props.byTask}
                canWrite={props.canWrite}
                epics={cardEpicsFromJobs(box())}
                simplified={props.sizes.simplified(id)}
                onAnswer={props.onAnswer}
                onSimplify={(simplified) => props.sizes.setCard(id, simplified)}
                notify={props.notify}
                send={props.send}
                writes={props.writes}
                onManage={props.onManage}
                onRename={props.onRename}
                onRemove={props.onRemove}
                onDrain={props.onDrain}
                onUndrain={props.onUndrain}
              />
            )}
          </Show>
        )}
      </For>
    </ul>
  );
}
